import { useEffect, useMemo, useRef, useState } from 'react'
import QuickTodoRow from './QuickTodoRow'
import { filterByKind } from '../../lib/noteFilters'

const ARCHIVE_LIMIT = 20

export default function QuickTodoBoard({ notes, savingIds, createQuickTodo, patchNote, archiveQuickTodo, deleteQuickTodo }) {
    const activeItems = useMemo(
        () => filterByKind(notes, 'quicktodo').filter((n) => !n.archived).sort((a, b) => a.order - b.order),
        [notes]
    )
    const archivedItems = useMemo(
        () =>
            filterByKind(notes, 'quicktodo')
                .filter((n) => n.archived)
                .sort((a, b) => new Date(b.archivedAt || 0) - new Date(a.archivedAt || 0)),
        [notes]
    )

    const [draftText, setDraftText] = useState('')
    const [pendingFocusId, setPendingFocusId] = useState(null)
    const rowRefs = useRef(new Map())
    const draftRef = useRef(null)

    const setRowRef = (id) => (node) => {
        if (node) rowRefs.current.set(id, node)
        else rowRefs.current.delete(id)
    }

    useEffect(() => {
        if (!pendingFocusId) return
        const node = rowRefs.current.get(pendingFocusId)
        if (node) {
            node.focus()
            node.setSelectionRange(node.value.length, node.value.length)
            setPendingFocusId(null)
        }
    }, [activeItems, pendingFocusId])

    const commitDraft = async () => {
        const text = draftText.trim()
        if (!text) return
        setDraftText('')
        draftRef.current?.focus()
        const order = activeItems.length ? activeItems[activeItems.length - 1].order + 1 : 0
        await createQuickTodo(text, order)
    }

    const insertAfter = async (index, currentText) => {
        const current = activeItems[index]
        if (currentText !== current.title) patchNote(current._id, { title: currentText })
        const next = activeItems[index + 1]
        const order = next ? (current.order + next.order) / 2 : current.order + 1
        const created = await createQuickTodo('', order)
        if (created) setPendingFocusId(created._id)
    }

    const handleBackspaceEmpty = (index) => {
        const item = activeItems[index]
        deleteQuickTodo(item._id)
        const prev = activeItems[index - 1]
        if (prev) setPendingFocusId(prev._id)
        else draftRef.current?.focus()
    }

    const handleCheck = (item) => {
        setTimeout(() => archiveQuickTodo(item._id), 300)
    }

    return (
        <div className="mt-6 max-w-2xl mx-auto">
            <div className="min-h-[60vh] border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4">
                {activeItems.length === 0 && (
                    <p className="text-sm text-zinc-500 mb-2">No quick todos yet — type below and press Enter.</p>
                )}
                {activeItems.map((item, index) => (
                    <QuickTodoRow
                        key={item._id}
                        value={item.title}
                        checked={!!item.done}
                        saving={savingIds.has(item._id)}
                        registerRef={setRowRef(item._id)}
                        onValueChange={(text) => patchNote(item._id, { title: text })}
                        onEnter={() => insertAfter(index, item.title)}
                        onBackspaceEmpty={() => handleBackspaceEmpty(index)}
                        onCheck={() => handleCheck(item)}
                    />
                ))}
                <QuickTodoRow
                    value={draftText}
                    registerRef={(node) => { draftRef.current = node }}
                    onValueChange={setDraftText}
                    onEnter={commitDraft}
                    placeholder="Type a todo and press Enter…"
                />
            </div>

            {archivedItems.length > 0 && (
                <div className="mt-8">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                        Archived ({archivedItems.length}/{ARCHIVE_LIMIT})
                    </h2>
                    <div className="border-2 border-black dark:border-white bg-white dark:bg-zinc-950 p-4 opacity-80">
                        {archivedItems.map((item) => (
                            <QuickTodoRow key={item._id} value={item.title} checked disabled />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
